import { css, cx } from "@emotion/css";
import { IconButton, LegacyListItemButton, Tooltip } from "@octopusdeploy/design-system-components";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { DeploymentActionResource, DeploymentStepResource } from "@octopusdeploy/octopus-server-client";
import { SpecialVariables, StartTrigger } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { isMatchingProcessTemplate } from "~/areas/projects/components/Process/Common/CommonProcessHelpers";
import { useActionTemplatesFromContext } from "~/areas/projects/components/Process/Contexts/ProcessActionTemplatesContextProvider";
import { useProcessBlueprintsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessBlueprintsContextProvider";
import { useProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { useProcessErrorSelectors } from "~/areas/projects/components/Process/Contexts/ProcessErrors/ProcessErrorsContext";
import { useProcessQueryStringContext } from "~/areas/projects/components/Process/Contexts/ProcessQueryString/ProcessQueryStringContext";
import { useProcessWarningSelectors } from "~/areas/projects/components/Process/Contexts/ProcessWarnings/ProcessWarningsContext";
import { ProcessActionErrorIndicator } from "~/areas/projects/components/Process/ErrorIndicators/ProcessActionErrorIndicator";
import { ProcessActionWarningIndicator } from "~/areas/projects/components/Process/ErrorIndicators/ProcessActionWarningIndicator";
import deploymentPartStyles from "~/areas/projects/components/Process/ListItems/ProcessListItem.module.less";
import deploymentPartForSidebarStyles from "~/areas/projects/components/Process/ListItems/ProcessListItemForSidebar.module.less";
import BlueprintSidebarLink from "~/areas/projects/components/Process/ProcessTemplates/BlueprintSidebarLink";
import getActionLogoUrl from "~/areas/projects/components/getActionLogoUrl";
import { StepRolling } from "~/components/Images/Process/StepRolling";
interface BlueprintActionSidebarItemProps {
    action: DeploymentActionResource;
    containingProcessTemplateStepName: string;
    containingProcessTemplateActionId: string;
    step: DeploymentStepResource;
    sidebarListing: string;
    isCurrent: boolean;
    isDisabled: boolean;
    isChild: boolean;
    stepIndex: number;
    actionIndex: number;
    blueprintSlug: string;
    blueprintVersionMask: string;
}
const BlueprintActionSidebarItem: React.FC<BlueprintActionSidebarItemProps> = (props) => {
    const actionTemplates = useActionTemplatesFromContext();
    const action = props.action;
    const step = props.step;
    const isChildAction = props.isChild;
    let actionTypeName = action.ActionType;
    const actionTemplate = actionTemplates.find((x) => x.Type === action.ActionType);
    if (actionTemplate) {
        actionTypeName = actionTemplate.Name;
    }
    const { actions: queryStringActions } = useProcessQueryStringContext();
    const onClick = React.useCallback(() => {
        queryStringActions.showProcessBlueprintAction(props.blueprintSlug, props.blueprintVersionMask, action.Id, props.containingProcessTemplateActionId);
        window.scroll({ top: 0, left: 0, behavior: "smooth" });
        //The current query string actions are tied to the same context as selectors. In order to avoid onClick breaking
        //memoization, we should move the actions into its own separate context.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.blueprintSlug, props.blueprintVersionMask, action.Id, props.containingProcessTemplateActionId]);
    return (<BlueprintSidebarLink actionType={actionTypeName} logoUrl={getActionLogoUrl(action)} isCurrentAction={props.isCurrent} sidebarListing={props.sidebarListing} stepIndex={props.stepIndex} actionIndex={props.actionIndex} isDisabled={props.isDisabled} isChild={props.isChild} name={action.Name} isRunInParallelWithLast={step.StartTrigger === StartTrigger.StartWithPrevious && !isChildAction} onClick={onClick} containingProcessTemplateStepName={props.containingProcessTemplateStepName}/>);
};
BlueprintActionSidebarItem.displayName = "BlueprintActionSidebarItem"
interface BlueprintParentStepSidebarItemProps {
    containingProcessTemplateStepName: string;
    containingProcessTemplateActionId: string;
    step: DeploymentStepResource;
    isCurrent: boolean;
    isDisabled: boolean;
    sidebarListing: string;
    stepIndex: number;
    renderChildAction: (action: DeploymentActionResource, step: DeploymentStepResource, sidebarListing: string, stepIndex: number, actionIndex: number, blueprintSlug: string, blueprintVersionMask: string) => React.ReactNode;
    blueprintSlug: string;
    blueprintVersionMask: string;
}
const BlueprintParentStepSidebarItem: React.FC<BlueprintParentStepSidebarItemProps> = ({ step, sidebarListing, stepIndex, isCurrent, isDisabled, renderChildAction, blueprintSlug, blueprintVersionMask, containingProcessTemplateStepName, containingProcessTemplateActionId, }) => {
    const maxParallelism = step.Properties[SpecialVariables.Action.MaxParallelism];
    const showWindowSize = maxParallelism ? maxParallelism.toString().length > 0 : false;
    const { actions: queryStringActions } = useProcessQueryStringContext();
    const onClick = React.useCallback(() => {
        queryStringActions.showProcessBlueprintParentStep(blueprintSlug, blueprintVersionMask, step.Id, containingProcessTemplateActionId);
        window.scroll({ top: 0, left: 0, behavior: "smooth" });
        //The current query string actions are tied to the same context as selectors. In order to avoid onClick breaking
        //memoization, we should move the actions into its own separate context.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blueprintSlug, blueprintVersionMask, step.Id]);
    const parentStepLabel = React.useMemo(() => showWindowSize ? (<span>Rolling deployment</span>) : (<span>
                    Multi-step deployment across
                    <br />
                    deployment targets
                </span>), [showWindowSize]);
    const icon = React.useMemo(() => <StepRolling height="1.6rem" className={deploymentPartStyles.stepIcon}/>, []);
    return (<div key={step.Id} className={deploymentPartForSidebarStyles.group}>
            <BlueprintSidebarLink actionType={parentStepLabel} icon={icon} isCurrentAction={isCurrent} sidebarListing={sidebarListing} isDisabled={isDisabled} name={step.Name} stepIndex={stepIndex} isRunInParallelWithLast={step.StartTrigger === StartTrigger.StartWithPrevious} onClick={onClick} containingProcessTemplateStepName={containingProcessTemplateStepName} isChild={false}/>
            {step.Actions.map((action, i) => {
            return renderChildAction(action, step, `${sidebarListing}${i + 1}.`, stepIndex, i, blueprintSlug, blueprintVersionMask);
        })}
        </div>);
};
BlueprintParentStepSidebarItem.displayName = "BlueprintParentStepSidebarItem"
interface BlueprintSidebarItemProps {
    actionId: string;
    isCurrent: boolean;
    onShowContextMenu: (event: React.MouseEvent<Element, MouseEvent>, actionId: string) => void;
    blueprintSlug: string;
    blueprintVersionMask: string;
}
const BlueprintSidebarItem: React.FC<BlueprintSidebarItemProps> = ({ onShowContextMenu, blueprintSlug, blueprintVersionMask, actionId, isCurrent }) => {
    const { selectors } = useProcessContext();
    const processTemplateContext = useProcessBlueprintsFromContext();
    const action = selectors.getActionById(actionId);
    const containingProcessTemplateStep = selectors.getStepById(action.ParentId);
    const containingProcessTemplateStepNumber = selectors.getStepNumber(containingProcessTemplateStep.Id);
    const { actions: queryStringActions, state: { queryFilter }, } = useProcessQueryStringContext();
    const onClick = React.useCallback(() => {
        queryStringActions.showProcessAction(action.Id);
        window.scroll({ top: 0, left: 0, behavior: "smooth" });
        //The current query string actions are tied to the same context as selectors. In order to avoid onClick breaking
        //memoization, we should move the actions into its own separate context.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.Id]);
    const processErrorSelectors = useProcessErrorSelectors();
    const processWarningSelectors = useProcessWarningSelectors();
    //TODO: revisit, we should remove the selectors from `getActionErrors` and consider passing in the result of the associated selectors instead to avoid
    //having to get a new set of errors every time process state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const actionErrors = React.useMemo(() => processErrorSelectors.getActionErrors(action.Id, selectors), [action.Id, processErrorSelectors]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const actionWarnings = React.useMemo(() => processWarningSelectors.getActionWarnings(action.Id, selectors), [action.Id, processWarningSelectors]);
    const handleShowContextMenu = React.useCallback((event: React.MouseEvent<Element, MouseEvent>) => {
        onShowContextMenu(event, action.Id);
    }, [action.Id, onShowContextMenu]);
    const isStepDisabled = selectors.isActionDisabled(action.Id);
    const renderChildAction = React.useCallback((action: DeploymentActionResource, step: DeploymentStepResource, sidebarListing: string, stepIndex: number, actionIndex: number, blueprintSlug: string, blueprintVersionMask: string) => {
        return (<BlueprintActionSidebarItem key={action.Id} blueprintSlug={blueprintSlug} blueprintVersionMask={blueprintVersionMask} containingProcessTemplateActionId={actionId} sidebarListing={sidebarListing} action={action} step={step} isCurrent={queryFilter.blueprintSlug === blueprintSlug && queryFilter.blueprintVersionMask === blueprintVersionMask && queryFilter.blueprintActionId === action.Id} isDisabled={isStepDisabled || action.IsDisabled} isChild={true} containingProcessTemplateStepName={containingProcessTemplateStep.Name} stepIndex={stepIndex} actionIndex={actionIndex}/>);
    }, [actionId, queryFilter, isStepDisabled, containingProcessTemplateStep.Name]);
    const blueprint = processTemplateContext.find((pt) => isMatchingProcessTemplate(pt, blueprintSlug, blueprintVersionMask))?.processTemplate;
    const errors = [...actionErrors];
    if (!blueprint) {
        errors.push("Process template not found");
    }
    return (<div className={cx(stylesInt.blueprintContainer, stylesInt.blueprintItemBackground)}>
            <StepLink isDisabled={isStepDisabled} isCurrentAction={isCurrent} onClick={onClick}>
                {withDisabledTooltipWrapper(<div className={stylesInt.blueprintHeadingText}>{action.Name}</div>, isStepDisabled)}
                {errors.length > 0 && <ProcessActionErrorIndicator actionErrors={errors}/>}
                {actionWarnings.length > 0 && <ProcessActionWarningIndicator actionWarnings={actionWarnings}/>}
                <div className={stylesInt.blueprintHeadingOverflow}>
                    <IconButton icon="EllipsisVerticalIcon" accessibleName={"OverflowMenu"} onClick={handleShowContextMenu}/>
                </div>
            </StepLink>

            {blueprint?.Steps.map((step, i) => {
            const isCurrent = queryFilter.blueprintSlug === blueprint.Slug && queryFilter.blueprintVersionMask === blueprintVersionMask && queryFilter.blueprintActionId === action.Id;
            if (step.Actions.length === 1) {
                const action = step.Actions[0];
                return (<BlueprintActionSidebarItem key={action.Id} containingProcessTemplateStepName={containingProcessTemplateStep.Name} containingProcessTemplateActionId={actionId} blueprintSlug={blueprint.Slug} blueprintVersionMask={blueprintVersionMask} sidebarListing={`${containingProcessTemplateStepNumber}.${i + 1}.`} action={action} stepIndex={i} actionIndex={0} step={step} isCurrent={isCurrent} isDisabled={isStepDisabled || action.IsDisabled} isChild={false}/>);
            }
            return (<BlueprintParentStepSidebarItem key={step.Id} containingProcessTemplateStepName={containingProcessTemplateStep.Name} containingProcessTemplateActionId={actionId} blueprintSlug={blueprint.Slug} blueprintVersionMask={blueprintVersionMask} sidebarListing={`${containingProcessTemplateStepNumber}.${i + 1}.`} stepIndex={i} step={step} isCurrent={isCurrent} renderChildAction={renderChildAction} isDisabled={isStepDisabled}/>);
        })}
        </div>);
};
BlueprintSidebarItem.displayName = "BlueprintSidebarItem"
const stylesInt = {
    blueprintItemBackground: css({
        backgroundColor: `${themeTokens.color.background.secondary.default} !important`,
    }),
    blueprintHeadingText: css({
        padding: `${space[12]} ${space[12]} ${space[12]} ${space[12]}`,
        fontSize: "0.9rem",
        flexGrow: 1,
    }),
    blueprintHeadingOverflow: css({
        paddingRight: space[12],
    }),
    blueprintContainer: css({
        zIndex: 0,
    }),
    activeStep: css({
        color: `${themeTokens.color.text.link.default} !important`,
        fontWeight: 600,
    }),
    disabledStep: css({
        color: `${themeTokens.color.button.text.disabled} !important`,
    }),
    tooltip: css({
        width: "100%",
    }),
};
function withDisabledTooltipWrapper(children: React.ReactNode, isDisabled: boolean) {
    if (isDisabled) {
        return (<Tooltip content="Disabled" className={stylesInt.tooltip}>
                {children}
            </Tooltip>);
    }
    return children;
}
type StepLinkProps = React.PropsWithChildren<{
    isDisabled: boolean;
    isCurrentAction: boolean;
    onClick: () => void;
    className?: string;
}>;
const StepLinkInternal: React.FC<StepLinkProps> = ({ children, isDisabled, isCurrentAction, onClick, className }) => {
    return (<LegacyListItemButton className={cx(className, { [stylesInt.activeStep]: isCurrentAction, [stylesInt.disabledStep]: isDisabled })} onClick={onClick}>
            {children}
        </LegacyListItemButton>);
};
StepLinkInternal.displayName = "StepLinkInternal"
const StepLink = React.memo(StepLinkInternal);
export default React.memo(BlueprintSidebarItem);
